import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash/debounce";
import { FormControl, Box, Select, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

const currentYear = new Date().getFullYear();
const YEAR_SNAP_PADDING = 5;

const useStyles = makeStyles((theme) => ({
  select_input: {
    width: "312px",
    height: 50,
    marginLeft: 5,
    marginBottom: "16px",
    [theme.breakpoints.down("1020")]: {
      width: "100%",
    },
    [theme.breakpoints.down("899")]: {
      margin: 5,
      width: "65%",
    },
  },
  autocomplete_container: {
    height: 50,
    width: "312px",
    marginLeft: 5,
    [theme.breakpoints.down("1020")]: {
      width: "100%",
    },
    [theme.breakpoints.down("899")]: {
      width: "65%",
    },
  },
  autotype_select: {
    display: "flex",
    height: 50,
    alignItems: "center",
  },
  customTextField: {
    "& .MuiOutlinedInput-root": {
      padding: "6px !important",
    },
  },
}));

const DebouncedSelect = ({ fetchOptions,
    autotype,
    textValue,
    onSelectAutotype,
    autoTypeOptions,
    namesuggestions,
    titlesuggestions,
    titleInputProps,
    nameInputProps,
    setLastTitleSelection,
    setLastNameSelection,
    setIsAppending,
    setShowDisabled,
    setTextValue,
    lastTitleSelection,
    setLastQuery,
    onSnapYear,
  }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [optionList, setOptionList] = useState([]);
  const [inputLength, setInputLength] = useState(0);

  const fetchOptionsRef = useRef(fetchOptions);
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  useEffect(() => {
    const suggestions =
      autotype === "name" ? namesuggestions : titlesuggestions;
    setOptionList(suggestions);
    setLoading(!suggestions.length === 0);
  }, [namesuggestions, titlesuggestions, autotype]);

  const debouncedFetchOptions = useMemo(
    () =>
      debounce(async (query) => {
        setLoading(true);
        if (query.length >= 3) {
          await fetchOptionsRef.current(query);
        } else {
          setOptionList([]);
          setLoading(false);
        }
      }, 500),
    [autotype]
  );

  // Cancel any pending debounced fetch when the debounce is recreated or the
  // component unmounts, so it can't fire setState on an unmounted component.
  useEffect(() => () => debouncedFetchOptions.cancel(), [debouncedFetchOptions]);

  const handleInputChange = (event, value, reason) => {
    if (reason === "clear") {
      setInputLength(0);
      setOptionList([]);
      if (autotype === "title") {
        setLastTitleSelection({ primaryTitle: "", isModified: false, selected: false, fromSuggestion: false });
      } else {
        setLastNameSelection({ id: "", primaryName: "", baseName: "", birthYear: null, deathYear: null, selected: false, fromSuggestion: false });
        setIsAppending(false);
        setShowDisabled(true);
        setTextValue("");
      }
      setLastQuery((prevState) => ({ ...prevState, query: "" }));
      return;
    }
    if (event && (reason === "input" || reason === "reset")) {
      setInputLength(value.length);
      if (reason === "input") {
        debouncedFetchOptions(value);
      }
      setLoading(false);
      if (autotype === "title") {
        setLastTitleSelection({
          primaryTitle: value,
          isModified: false,
          selected: true,
          fromSuggestion: false,
        });
      } else {
        setLastNameSelection({
          id: "",
          primaryName: "",
          baseName: "",
          birthYear: null,
          deathYear: null,
          selected: false,
          fromSuggestion: false,
        });
        setIsAppending(false);
        setShowDisabled(true);
        setTextValue(value);
      }
      setLastQuery((prevState) => ({ ...prevState, query: value }));
    }
  };

  const handleOptionChange = (event, value) => {
    if (autotype === "title") {
      if (value) {
        setLastTitleSelection({
          primaryTitle: value.primaryTitle,
          isModified: false,
          selected: true,
          fromSuggestion: true,
        });
        if (value.startYear != null) {
          onSnapYear([value.startYear, Math.min(value.startYear + YEAR_SNAP_PADDING, currentYear)]);
        }
      } else {
        setLastTitleSelection({
          primaryTitle: "",
          isModified: false,
          selected: false,
          fromSuggestion: false,
        });
      }
    } else if (autotype === "name") {
      if (value) {
        const baseName = value?.baseName ?? "";
        const birthYear = value?.birthYear ?? null;
        const deathYear = value?.deathYear ?? null;

        setLastNameSelection({
          id: value?.id ?? "",
          primaryName: value?.primaryName ?? baseName,
          baseName,
          birthYear,
          deathYear,
          selected: true,
          fromSuggestion: true,
        });
        setTextValue(baseName);
        setLastQuery((prevState) => ({ ...prevState, query: value?.id ?? baseName }));
        if (birthYear != null) {
          onSnapYear([birthYear, deathYear ?? currentYear]);
        }
        setShowDisabled(false);
      } else {
        setLastNameSelection({
          id: "",
          primaryName: "",
          baseName: "",
          birthYear: null,
          deathYear: null,
          selected: false,
          fromSuggestion: false,
        });
        setTextValue("");
        setLastQuery((prevState) => ({ ...prevState, query: "" }));
        setShowDisabled(true);
      }

      setIsAppending(false);
    }
  };

  const onTitleChange = (event) => {
    let value = event.target.value;
    if (
      lastTitleSelection.selected &&
      value !== lastTitleSelection.primaryTitle
    ) {
      setLastTitleSelection((prevState) => ({
        ...prevState,
        primaryTitle: value,
        isModified: true,
      }));
    }
  };

  const onNameChange = (event) => {
    let value = event.target.value;
    setIsAppending(false);
    setShowDisabled(false);
    setTextValue(value);
    setLastQuery((prevState) => ({ ...prevState, query: value }));
  };

  return (
    <>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.select_input}
      >
        <Select
          className={classes.autotype_select}
          id="autotype-select"
          value={autotype}
          onChange={(e) => onSelectAutotype(e.target.value)}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            }
          }}
        >
          {autoTypeOptions()}
        </Select>
      </FormControl>
      <Box className={classes.autocomplete_container}>
        <Autocomplete
          className={classes.autocomplete}
          inputValue={autotype === "title" ? lastTitleSelection.primaryTitle : textValue}
          noOptionsText={
            <Typography variant="caption">
              {inputLength > 0 && inputLength < 3
                ? "Minimum 3 chars"
                : "No matches - change filters"}
            </Typography>
          }
          getOptionLabel={(option) =>
            autotype !== "title"
              ? option.primaryName ?? ""
              : option.primaryTitle
          }
          renderOption={(props, option) => (
            <li {...props}>
              {autotype !== "title"
                ? option.primaryName ?? ""
                : option.startYear
                  ? `${option.primaryTitle} (${option.startYear})`
                  : option.primaryTitle}
            </li>
          )}
          options={optionList}
          onChange={handleOptionChange}
          loading={loading}
          freeSolo
          onInputChange={handleInputChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                autotype === "title"
                  ? titleInputProps?.placeholder
                  : nameInputProps?.placeholder
              }
              onChange={autotype === "title" ? onTitleChange : onNameChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
              className={classes.customTextField}
            />
          )}
        />
      </Box>
    </>
  );
}

export default DebouncedSelect
